import { RefObject, useCallback, useEffect, useRef } from 'react';

type UsePulseElementOptions = {
  boxShadow?: string;
  borderRadius?: string;
  pulseCount?: number;
  onDurationMs?: number;
  offDurationMs?: number;
  transitionMs?: number;
};

/**
 * Returns a trigger that flashes a box-shadow on the referenced element to draw
 * attention to it (e.g. a required field the user skipped).
 */
const usePulseElement = <T extends HTMLElement>(
  ref: RefObject<T | null>,
  {
    boxShadow = '0 0 10px var(--global-error-500)',
    borderRadius,
    pulseCount = 3,
    onDurationMs = 400,
    offDurationMs = 200,
    transitionMs = 200,
  }: UsePulseElementOptions = {}
) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const restoreRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      restoreRef.current?.();
    };
  }, []);

  return useCallback(() => {
    const el = ref.current;
    if (!el) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // If a previous pulse is still active, restore its snapshot before taking a new one
    // so we don't snapshot our own in-flight mutations as the "original".
    restoreRef.current?.();

    const originalTransition = el.style.transition;
    const originalBoxShadow = el.style.boxShadow;
    const originalBorderRadius = el.style.borderRadius;

    const restore = () => {
      el.style.transition = originalTransition;
      el.style.boxShadow = originalBoxShadow;
      el.style.borderRadius = originalBorderRadius;
      restoreRef.current = null;
    };
    restoreRef.current = restore;

    el.style.transition = `box-shadow ${transitionMs}ms ease-in-out`;
    if (borderRadius) {
      el.style.borderRadius = borderRadius;
    }

    let count = 0;

    const pulseOff = () => {
      el.style.boxShadow = originalBoxShadow;
      count += 1;
      if (count < pulseCount) {
        timeoutRef.current = setTimeout(pulseOn, offDurationMs);
      } else {
        timeoutRef.current = setTimeout(restore, transitionMs);
      }
    };

    const pulseOn = () => {
      el.style.boxShadow = boxShadow;
      timeoutRef.current = setTimeout(pulseOff, onDurationMs);
    };

    pulseOn();
  }, [ref, boxShadow, borderRadius, pulseCount, onDurationMs, offDurationMs, transitionMs]);
};

export default usePulseElement;
